export type ProductKey = "trailer-repair" | "axles-for-sale" | "trailer-axles" | "parts";

export interface ProductDef {
  key: ProductKey;
  slug: string;
  label: string;
  shortLabel: string;
  motif: "wrench" | "shop" | "axle" | "shelves";
  h1: (city: string, state: string) => string;
  metaTitle: (city: string, state: string) => string;
  metaDesc: (city: string, state: string) => string;
  pitch: string;
  features: { title: string; desc: string }[];
  /** FAQs use {{city}} placeholder for runtime interpolation */
  faqs: { q: string; a: string }[];
  schemaType: "Service" | "Product" | "Service" | "Service";
  serviceType: string;
}

export const PRODUCTS: ProductDef[] = [
  {
    key: "trailer-repair",
    slug: "trailer-repair",
    label: "Trailer Repair",
    shortLabel: "Repair",
    motif: "wrench",
    h1: (c, s) => `Trailer Repair in ${c}, ${s}`,
    metaTitle: (c, s) => `Trailer Repair in ${c}, ${s} - Bearings, Brakes, Spindles`,
    metaDesc: (c, s) =>
      `Trailer repair serving ${c}, ${s}: bearing repacks, brake service, hub replacement, spindle work, and full axle replacement up to 10,000 lb. Schedule online or call (574) 264-9434.`,
    pitch:
      "Bearing repacks, electric and hydraulic brake service, hub and spindle replacement, suspension overhauls, and full axle replacement on light-duty trailers up to 10,000 lb. Same-day turnaround on routine work, OEM Dexter parts, and a sixty-plus-year combined experience base behind every job.",
    features: [
      { title: "Bearing repack & replacement", desc: "Inner and outer bearings, races, and seals - repacked or replaced with the right grease for the application (marine-grade for boat trailers, NLGI #2 for general use)." },
      { title: "Electric & hydraulic brake service", desc: "Magnets, shoes, drums, hydraulic actuators, and full backing-plate assemblies on Dexter, Lippert, and most other major brands." },
      { title: "Hub & spindle work", desc: "Cut-and-weld spindle replacement, complete hub assemblies, and wheel-stud repairs on Dexter and other major brands." },
      { title: "Suspension repair", desc: "Springs, shackles, hangers, equalizers. Convert slipper to double-eye or upgrade for added capacity." },
    ],
    faqs: [
      { q: "How long does a typical trailer repair take for {{city}} customers?", a: "Most routine repairs (bearing repack, brake shoe replacement, hub swap) are same-day if the trailer is dropped before 10am. Full axle replacements on {{city}} trailers typically take 2 to 4 business days depending on parts availability. We'll give you an honest timeline at drop-off." },
      { q: "Do I need an appointment for trailer repair from {{city}}?", a: "Yes for service work - schedule online at axle.setmore.com or call the shop. Walk-ins for parts are welcome during business hours. {{city}} customers typically schedule a week ahead in spring and fall for routine service." },
      { q: "What's the most common repair you do for {{city}} trailers?", a: "Bearing repacks, by a wide margin. They're the cheapest service that prevents the most expensive failures, and almost every {{city}} trailer that comes to us for the first time needs at least an inspection." },
    ],
    schemaType: "Service",
    serviceType: "Trailer Repair",
  },
  {
    key: "axles-for-sale",
    slug: "axles-for-sale",
    label: "Axles for Sale",
    shortLabel: "Axles for Sale",
    motif: "shop",
    h1: (c, s) => `Trailer Axles for Sale in ${c}, ${s}`,
    metaTitle: (c, s) => `Trailer Axles for Sale in ${c}, ${s} - Dexter OEM, 1,000 to 10,000 lb`,
    metaDesc: (c, s) =>
      `Buy trailer axles in ${c}, ${s}: authorized Dexter distributor with 1,000 to 10,000 lb capacity in stock. Idler or brake configurations, hubs and running gear matched. Call (574) 264-9434.`,
    pitch:
      "Authorized Dexter Group distributor stocking trailer axles from 1,000 to 10,000 lb capacity in idler and electric-brake configurations. Walk in, call, or order ahead - most standard configurations are ready the same day, with hubs, bearings, brakes, and running gear matched at point of sale.",
    features: [
      { title: "Capacity range", desc: "1,000 lb idlers up to 10,000 lb tandem applications. Single, tandem, and triple-axle setups available." },
      { title: "Brake options", desc: "Idler (no brakes), electric drum, hydraulic drum, and hydraulic disc. Magnet-grade and assembly-grade brakes available." },
      { title: "Hub configurations", desc: "5-on-4.5, 5-on-5, 6-on-5.5, and 8-on-6.5 bolt patterns. Hubs and complete running gear in matched kits." },
      { title: "Dexter rebates", desc: "Current OEM pricing and rebates available - significant savings vs aftermarket alternatives. Ask at the counter or check /rebates/." },
    ],
    faqs: [
      { q: "Where can I buy a trailer axle near {{city}}?", a: "Axle Inc. in Elkhart, IN - the closest authorized Dexter Group distributor to {{city}}. We carry capacity ratings from 1,000 to 10,000 lb in idler or brake configurations, with hubs and running gear in matched kits." },
      { q: "What does a replacement trailer axle cost for a {{city}} customer?", a: "Pricing depends on capacity, brake type, and hub configuration. A standard 3,500 lb idler axle starts in the low hundreds; 5,200 lb electric-brake configurations are the volume seller. Call the shop with your spec or stop by the counter and we'll quote on the spot." },
      { q: "Do you ship trailer axles to {{city}}?", a: "Most {{city}} customers pick up directly given our proximity, but we can arrange local delivery for larger orders or fleet accounts. Call the shop to discuss." },
    ],
    schemaType: "Product",
    serviceType: "Trailer Axle Sales",
  },
  {
    key: "trailer-axles",
    slug: "trailer-axles",
    label: "Trailer Axles",
    shortLabel: "Trailer Axles",
    motif: "axle",
    h1: (c, s) => `Replacement & Custom Trailer Axles for ${c}, ${s}`,
    metaTitle: (c, s) => `Trailer Axles for ${c}, ${s} - Replacement & Custom Builds`,
    metaDesc: (c, s) =>
      `Replacement and custom trailer axles serving ${c}, ${s}. Authorized Dexter distributor - capacity, length, hub face, and brake type built to match. Call (574) 264-9434.`,
    pitch:
      "Replacement and custom-built trailer axles for ${c} customers, sourced from Dexter Group or built locally to your exact specifications. Bring the spec plate, the old axle, or the trailer itself - we measure, match, and deliver. Standard configurations ship the same day; custom builds turn around in 3 to 7 business days.",
    features: [
      { title: "Replacement axles", desc: "Direct Dexter equivalents to your existing axle - same capacity, same hub face, same brake configuration. Bolt-on installation, no frame modification." },
      { title: "Custom axle builds", desc: "Non-standard length, unusual hub face, specific brake actuation. Built locally to your drawing or sample axle." },
      { title: "Capacity matching", desc: "We won't undersize. If your loaded weight runs near or above the existing axle's capacity, we'll quote the right capacity instead and explain why." },
      { title: "Running gear matched", desc: "Hubs, bearings, brake drums, and complete kits matched at point of sale. No surprises at install." },
    ],
    faqs: [
      { q: "Can you build a custom trailer axle for a {{city}} customer?", a: "Yes - non-standard lengths, unusual hub faces, and specific brake configurations are routine work. Provide a drawing, photos of the existing axle, or bring the trailer in. Most custom builds for {{city}} customers turn around in 3 to 7 business days." },
      { q: "How do I measure a trailer axle for replacement?", a: "Measure the hub face to hub face dimension, the spring center distance, the bolt pattern (count studs and measure across), and photograph the spec plate. We'll match the configuration. {{city}} customers can email photos before the trip and we'll quote ahead." },
      { q: "What capacity trailer axle do I need?", a: "Match the axle to your real loaded weight, not your factory GVWR plate. We'll inspect the trailer, ask about typical load, and recommend a capacity with appropriate margin. Undersized axles fail; oversized ones are wasted money - we steer between." },
    ],
    schemaType: "Service",
    serviceType: "Trailer Axle Replacement",
  },
  {
    key: "parts",
    slug: "parts",
    label: "Trailer Parts",
    shortLabel: "Parts",
    motif: "shelves",
    h1: (c, s) => `Trailer Parts in ${c}, ${s}`,
    metaTitle: (c, s) => `Trailer Parts in ${c}, ${s} - Walk-in Counter, OEM Stock`,
    metaDesc: (c, s) =>
      `Trailer parts serving ${c}, ${s}: hubs, bearings, brakes, lights, jacks, couplers, and electrical. Walk-in counter open during business hours. Call (574) 264-9434.`,
    pitch:
      "Walk-in trailer parts counter stocked with the OEM-Dexter and equivalent components your trailer actually needs - not the automotive substitutes the chain auto-parts shops carry. Hubs, bearings, brake assemblies, lights, jacks, couplers, suspension parts, and electrical hardware all under one roof.",
    features: [
      { title: "Hubs & bearings", desc: "Complete hub kits, sealed-hub conversions, individual bearings, races, seals, and dust caps for all common Dexter and Lippert spindle sizes." },
      { title: "Brake hardware", desc: "Electric brake magnets, shoes, drums, full backing-plate assemblies, hydraulic actuators, and brake controllers." },
      { title: "Lights & electrical", desc: "DOT-approved tail/brake/turn assemblies, LED conversions, 4-flat and 7-pin connectors, junction boxes, and replacement wiring kits." },
      { title: "Couplers & jacks", desc: "Straight-tongue and adjustable couplers, manual and electric jacks, hardware, and accessories." },
    ],
    faqs: [
      { q: "Can I walk in for trailer parts from {{city}}?", a: "Yes - our parts counter is open during business hours for {{city}} customers and walk-ins from across the region. Bring the old part or a photo of the spec plate and we'll match it on the spot." },
      { q: "Do you carry trailer hardware that auto-parts stores don't?", a: "Yes - that's much of what we sell. Trailer hubs, bearings, brake hardware, and electrical components are spec'd differently from automotive equivalents, and using the wrong part costs you a return trip. {{city}} customers come to us specifically for this reason." },
      { q: "Do you order trailer parts that aren't in stock?", a: "Yes - most non-stocked parts arrive in 2 to 5 business days from our Dexter and supplier networks. Call ahead and we'll start the order before you make the drive from {{city}}." },
    ],
    schemaType: "Service",
    serviceType: "Trailer Parts Sales",
  },
];

export const PRODUCT_KEYS = PRODUCTS.map((p) => p.key);

export function getProduct(key: string): ProductDef | undefined {
  return PRODUCTS.find((p) => p.key === key);
}

export function interpolate(text: string, city: string): string {
  return text.replace(/\{\{city\}\}/g, city);
}

import { PRODUCTS_ES } from "./products.es.ts";
import type { Locale } from "@/i18n/locale";

export function getProducts(locale: Locale = "en"): ProductDef[] {
  return locale === "es" ? PRODUCTS_ES : PRODUCTS;
}
export function getProductL(key: string, locale: Locale = "en"): ProductDef | undefined {
  return getProducts(locale).find((p) => p.key === key);
}
